import { useCallback, useEffect, useRef } from 'react';
import { cn } from '@/utils/cn';
import { setupCanvas } from '@/utils/canvasHelpers';
import type { LandmarkDeviationWithCoords } from '@/types/visualization';
import {
  getThresholdScoreColor,
  THERMAL_SILHOUETTE,
  THERMAL_EFFECTS,
  SCORE_THRESHOLDS,
  RENDERING,
  isLandmarkVisible,
  addHexAlpha,
} from '@/utils/visualizationConstants';

interface ThermalSilhouetteOverlayProps {
  canvasRef?: React.RefObject<HTMLCanvasElement | null>;
  landmarks: Array<{ x: number; y: number; visibility?: number }>;
  landmarkDeviations?: LandmarkDeviationWithCoords[];
  /** Width of the actual video render area */
  width: number;
  /** Height of the actual video render area */
  height: number;
  /** X offset from canvas left edge to the video render area (pillarbox offset) */
  offsetX?: number;
  /** Y offset from canvas top edge to the video render area (letterbox offset) */
  offsetY?: number;
  /** Full container width — canvas is sized to this */
  containerWidth?: number;
  /** Full container height — canvas is sized to this */
  containerHeight?: number;
  intensity: number;
  currentFrame: number;
  fallbackScore?: number | null; // Frame-level score to use when no per-landmark data
  className?: string;
}

// MediaPipe landmark indices for body regions
const BODY_REGIONS = {
  torso: [11, 12, 24, 23], // left shoulder, right shoulder, right hip, left hip
  leftArm: [11, 13, 15], // left shoulder, left elbow, left wrist
  rightArm: [12, 14, 16], // right shoulder, right elbow, right wrist
  leftLeg: [23, 25, 27], // left hip, left knee, left ankle
  rightLeg: [24, 26, 28], // right hip, right knee, right ankle
};

/**
 * ThermalSilhouetteOverlay renders a body silhouette filled with heat-map coloring
 * based on alignment quality. High scores appear in teal, medium in amber, low in red.
 */
function ThermalSilhouetteOverlay({
  canvasRef: externalCanvasRef,
  landmarks,
  landmarkDeviations = [],
  width,
  height,
  offsetX = 0,
  offsetY = 0,
  containerWidth,
  containerHeight,
  intensity,
  currentFrame,
  fallbackScore,
  className,
}: ThermalSilhouetteOverlayProps) {
  const internalCanvasRef = useRef<HTMLCanvasElement>(null);
  const canvasRef = externalCanvasRef || internalCanvasRef;
  const canvasW = containerWidth ?? width;
  const canvasH = containerHeight ?? height;


  // Calculate orientation of ordered triplet (p, q, r)
  // Returns: 0 -> p, q, r are colinear; 1 -> Clockwise; 2 -> Counterclockwise
  // Pure function of its arguments (no closures) — stable identity forever.
  const orientation = useCallback((
    p: { x: number; y: number },
    q: { x: number; y: number },
    r: { x: number; y: number }
  ): number => {
    const val = (q.y - p.y) * (r.x - q.x) - (q.x - p.x) * (r.y - q.y);
    if (val === 0) return 0;
    return val > 0 ? 1 : 2;
  }, []);

  // Calculate convex hull for a set of points (Gift wrapping algorithm).
  // Pure function of its `points` argument — stable identity forever.
  const computeConvexHull = useCallback((points: Array<{ x: number; y: number }>): Array<{ x: number; y: number }> => {
    if (points.length < 3) return points;

    // Find the leftmost point
    let leftmost = 0;
    for (let i = 1; i < points.length; i++) {
      if (points[i].x < points[leftmost].x) {
        leftmost = i;
      }
    }

    const hull: Array<{ x: number; y: number }> = [];
    let p = leftmost;
    let q: number;

    do {
      hull.push(points[p]);
      q = (p + 1) % points.length;

      for (let i = 0; i < points.length; i++) {
        if (orientation(points[p], points[i], points[q]) === 2) {
          q = i;
        }
      }

      p = q;
    } while (p !== leftmost);

    return hull;
  }, [orientation]);

  // Get average deviation score for a region's landmarks. Memoized on the
  // values it actually reads so the draw effect below picks up fresh scores
  // (including `fallbackScore`) without redrawing on every render.
  const getRegionScore = useCallback((landmarkIndices: number[]): number => {
    const frameDeviations = (landmarkDeviations ?? []).filter(d => d.frame_index === currentFrame);

    const regionDeviations = frameDeviations.filter(d =>
      landmarkIndices.includes(d.landmark_id)
    );

    if (regionDeviations.length === 0) {
      // Use fallback frame score if available, otherwise default to neutral
      if (fallbackScore !== null && fallbackScore !== undefined) {
        return fallbackScore;
      }
      return SCORE_THRESHOLDS.MEDIUM;
    }

    const avgScore = regionDeviations.reduce((sum, d) => sum + d.deviation_score, 0) / regionDeviations.length;
    return avgScore;
  }, [landmarkDeviations, currentFrame, fallbackScore]);

  // Draw the thermal silhouette
  useEffect(() => {
    const ctx = setupCanvas(canvasRef, canvasW, canvasH);
    if (!ctx) return;

    try {
      // Skip rendering if intensity is too low (nearly invisible)
      if (intensity < RENDERING.MIN_INTENSITY) return;

      // Ensure we have enough landmarks
      if (!landmarks || landmarks.length < 33) return;

      // Calculate opacity based on intensity (0-100 -> 0-1)
      const opacity = intensity / 100;

    // Process each body region
    Object.entries(BODY_REGIONS).forEach(([_regionName, landmarkIndices]) => {
      // Get region landmarks
      const regionLandmarks = landmarkIndices
        .map(idx => landmarks[idx])
        .filter(lm => lm && isLandmarkVisible(lm));

      if (regionLandmarks.length < THERMAL_SILHOUETTE.MIN_HULL_POINTS) return; // Need at least 3 points for a hull

      // Convert to pixel coordinates (apply video render area offset)
      const points = regionLandmarks.map(lm => ({
        x: lm.x * width + offsetX,
        y: lm.y * height + offsetY,
      }));

      // Compute convex hull
      const hull = computeConvexHull(points);
      if (hull.length < THERMAL_SILHOUETTE.MIN_HULL_POINTS) return;

      // Get average deviation score for this region
      const regionScore = getRegionScore(landmarkIndices);
      const color = getThresholdScoreColor(regionScore);

      // Calculate center for gradient
      const centerX = hull.reduce((sum, p) => sum + p.x, 0) / hull.length;
      const centerY = hull.reduce((sum, p) => sum + p.y, 0) / hull.length;

      // Calculate approximate radius for gradient
      const maxDist = Math.max(
        ...hull.map(p => Math.sqrt((p.x - centerX) ** 2 + (p.y - centerY) ** 2))
      );

      // Create radial gradient for thermal effect
      const gradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, maxDist);
      gradient.addColorStop(0, color);
      gradient.addColorStop(1, addHexAlpha(color, THERMAL_EFFECTS.GRADIENT_HEX_ALPHA)); // Fade out at edges

      // Draw filled hull with gradient
      ctx.save();
      ctx.globalAlpha = opacity * THERMAL_SILHOUETTE.FILL_OPACITY_MULTIPLIER;
      ctx.fillStyle = gradient;

      ctx.beginPath();
      ctx.moveTo(hull[0].x, hull[0].y);
      for (let i = 1; i < hull.length; i++) {
        ctx.lineTo(hull[i].x, hull[i].y);
      }
      ctx.closePath();
      ctx.fill();
      ctx.restore();

      // Draw border with glow effect
      ctx.save();
      ctx.globalAlpha = opacity;
      ctx.strokeStyle = color;
      ctx.lineWidth = THERMAL_SILHOUETTE.SCAN_LINE_SPACING / 2;
      ctx.shadowBlur = THERMAL_EFFECTS.BORDER_GLOW_BLUR;
      ctx.shadowColor = color;

      ctx.beginPath();
      ctx.moveTo(hull[0].x, hull[0].y);
      for (let i = 1; i < hull.length; i++) {
        ctx.lineTo(hull[i].x, hull[i].y);
      }
      ctx.closePath();
      ctx.stroke();
      ctx.restore();
    });

    // Optional: Add subtle scan-line texture effect
    if (intensity > THERMAL_EFFECTS.SCAN_LINE_INTENSITY_THRESHOLD) {
      ctx.save();
      ctx.globalAlpha = opacity * THERMAL_EFFECTS.SCAN_LINE_OPACITY;
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = THERMAL_EFFECTS.SCAN_LINE_WIDTH;

      // Draw horizontal scan lines within the video render area only
      for (let y = offsetY; y < offsetY + height; y += THERMAL_SILHOUETTE.SCAN_LINE_SPACING) {
        ctx.beginPath();
        ctx.moveTo(offsetX, y);
        ctx.lineTo(offsetX + width, y);
        ctx.stroke();
      }
      ctx.restore();
      }
    } catch (error) {
      console.error('[ThermalSilhouetteOverlay] Canvas drawing error:', error);
    }
  }, [canvasRef, landmarks, landmarkDeviations, width, height, offsetX, offsetY, canvasW, canvasH, intensity, currentFrame, computeConvexHull, getRegionScore]);

  // Always render canvas - the useEffect handles drawing to whichever ref is used
  return (
    <canvas
      ref={canvasRef}
      width={canvasW}
      height={canvasH}
      className={cn('absolute inset-0 pointer-events-none', className)}
      style={{ width: '100%', height: '100%' }}
      aria-label="Thermal silhouette overlay"
    />
  );
}

export default ThermalSilhouetteOverlay;
